import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * v6.32.24 — Reusable production-impact disclosure for destructive Super
 * Admin actions. Renders a collapsible <details> block listing exactly what
 * the action affects vs. doesn't touch. Same visual pattern as the
 * v6.32.23 System Health Clean Cache disclosure, applied across the
 * Super Admin destructive-action surfaces (Cancel Stuck Audit, MIME Fix,
 * Reverify-All) so operators always see the same explicit production-
 * impact breakdown before clicking confirm.
 *
 * Props:
 *   removes      string[]  Bullets describing what changes / what's removed
 *   doesNotTouch string[]  Bullets describing what is guaranteed untouched
 *   note         string?   Optional italic footer note (e.g. multi-pod caveat)
 *   testId       string?   Optional data-testid override
 */
export const ProductionImpactDisclosure = ({
  removes = [],
  doesNotTouch = [],
  note,
  testId = 'production-impact-disclosure',
}) => (
  <details className="group" data-testid={testId}>
    <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700 select-none list-none flex items-center gap-1">
      <ChevronRight size={11} className="transition-transform group-open:rotate-90" />
      What does this actually do?
    </summary>
    <div className="mt-2 pl-3 border-l-2 border-gray-100 space-y-2 text-[11px] leading-relaxed">
      {removes.length > 0 && (
        <div>
          <div className="font-semibold text-emerald-700 uppercase tracking-wide text-[10px] mb-0.5">
            Affects
          </div>
          <ul className="text-gray-600 space-y-0.5 ml-2">
            {removes.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}
      {doesNotTouch.length > 0 && (
        <div>
          <div className="font-semibold text-rose-700 uppercase tracking-wide text-[10px] mb-0.5">
            Does NOT touch
          </div>
          <ul className="text-gray-600 space-y-0.5 ml-2">
            {doesNotTouch.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}
      {note && (
        <div className="text-gray-400 italic">{note}</div>
      )}
    </div>
  </details>
);

export default ProductionImpactDisclosure;
